import React from "react";
import { twMerge } from "tailwind-merge";
import { useMatch } from "react-router-dom";
import { Search, Folder } from "lucide-react";
import { NavLink } from "react-router-dom";
import { Button } from "@themes/pollinator/ui/Button";
import { PollinatorLogo } from "@themes/pollinator/ui/PollinatorLogo";
import { HorizontalLine } from "@themes/pollinator/ui/HorizontalLine";
import { PlatformAvatarDropdown } from "@themes/pollinator/ui/PlatformAvatarDropdown";
import { CountBadge } from "@themes/pollinator/ui/CountBadge";
import { Type } from "@themes/pollinator/ui/Type";
import { User } from "@platform/types/platform.types";
import { useChatVisibility } from "@platform/hooks/useChatVisibility";
import { ChatButtonContainer } from "@platform/features/chat";
import {
  profileNavigationItem,
  type NavigationItem,
} from "./dashboardNavigationItemsData";
import type { Circle } from "@core/types/core.types";
import type { OrganizationData } from "@core/features/actions";

/** Hook to check if a navigation item is active based on its href and optional matchPatterns */
function useIsNavItemActive(item: NavigationItem): boolean {
  const hrefMatch = useMatch(item.href);
  const pattern1Match = useMatch(item.matchPatterns?.[0] ?? "");
  const pattern2Match = useMatch(item.matchPatterns?.[1] ?? "");
  const pattern3Match = useMatch(item.matchPatterns?.[2] ?? "");

  return Boolean(hrefMatch || pattern1Match || pattern2Match || pattern3Match);
}

export type Props = {
  onOpenSearch: () => void;
  onNavigationClick: (
    e: React.MouseEvent<HTMLAnchorElement>,
    to: string
  ) => void;
  user: User | null;
  isChatDisabled: boolean;
  /** Whether the support page is disabled (hides Search and Support nav items) */
  isSupportPageDisabled: boolean;
  userCircles: Circle[] | null | undefined;
  /** Program ID for the invitations link (only shown if user has invite_friends action) */
  invitationProgramId: number | null;
  /** Program ID for the program link (shown if user has schedule or circle_view action) */
  programProgramId: number | null;
  /** Program ID for the resources link (shown when user has any program) */
  resourcesProgramId: number | null;
  /** Program ID for the profile link (always shown if user has any program) */
  profileProgramId: number | null;
  /** Program ID for the support link (shown if user has any program) */
  supportProgramId: number | null;
  /** Organizations the user can admin (shown as navigation links) */
  organizations: OrganizationData[];
  /** Count of incomplete visible actions (displayed as badge on Dashboard) */
  incompleteActionCount: number;
  /** Whether the user has video meetings (shows Video nav item) */
  hasVideoMeetings: boolean;
};

type NavItemButtonProps = {
  item: NavigationItem;
  onNavigationClick: Props["onNavigationClick"];
};

function NavItemButton({ item, onNavigationClick }: NavItemButtonProps) {
  const isActive = useIsNavItemActive(item);

  return (
    <Button
      as={NavLink}
      to={item.href}
      onClick={(e: React.MouseEvent<HTMLAnchorElement>) =>
        onNavigationClick(e, item.href)
      }
      variant="ghost"
      className={twMerge(
        "w-full justify-start",
        isActive && "bg-brand-100 rounded-full"
      )}
      data-testid={`dashboard-nav-${item.title.toLowerCase()}`}
    >
      {item.title}
    </Button>
  );
}

export function DashboardNavigationItems({
  onOpenSearch,
  onNavigationClick,
  user,
  isChatDisabled,
  isSupportPageDisabled,
  userCircles,
  invitationProgramId,
  programProgramId,
  resourcesProgramId,
  profileProgramId,
  supportProgramId,
  organizations,
  incompleteActionCount,
  hasVideoMeetings,
}: Props) {
  const fullName = `${user?.first_name} ${user?.last_name}`;
  const { shouldShowChat } = useChatVisibility({ userCircles, isChatDisabled });

  // Path computations
  const invitationsPath = invitationProgramId
    ? `/program/${invitationProgramId}/invitations`
    : null;
  const programPath = programProgramId
    ? `/program/${programProgramId}`
    : null;
  const resourcesPath = resourcesProgramId
    ? `/program/${resourcesProgramId}/resources`
    : null;
  const profilePath = profileProgramId
    ? `/program/${profileProgramId}/profile`
    : null;
  const supportPath = supportProgramId
    ? `/program/${supportProgramId}/support`
    : null;
  // Route matching for active state
  const invitationsMatch = useMatch("/program/:id/invitations");
  const programMatch = useMatch("/program/:id");
  const programTabMatch = useMatch("/program/:id/:tab");
  const resourcesMatch = useMatch("/program/:id/resources");
  const profileMatch = useMatch("/program/:id/profile");
  const profileTabMatch = useMatch("/program/:id/profile/:tab");
  const supportMatch = useMatch("/program/:id/support");
  const supportArticleMatch = useMatch("/program/:id/support/:articleId");
  const orgAdminMatch = useMatch("/program/:programId/organization/:orgId/admin");
  const orgAdminTabMatch = useMatch("/program/:programId/organization/:orgId/admin/:tab");
  const videoMatch = useMatch("/video/*");
  const accountMatch = useMatch("/account");

  return (
    <>
      {/* ===== TOP SECTION: Logo, Search, Notifications ===== */}
      <nav className="flex flex-col w-full justify-start items-start gap-2">
        <a
          href="/dashboard"
          className={twMerge("py-2 px-2", !isSupportPageDisabled && "mb-4")}
          data-testid="dashboard-nav-home"
          title="Dashboard"
          aria-label="Dashboard"
        >
          <PollinatorLogo size="sm" />
        </a>

        {/* Action buttons with icons */}
        {!isSupportPageDisabled && (
          <Button
            variant="ghost"
            iconComponent={Search}
            onClick={onOpenSearch}
            title="Open Support Search"
            className="w-full justify-start"
            data-testid="dashboard-nav-search"
          >
            Search
          </Button>
        )}
        {shouldShowChat && (
          <ChatButtonContainer containerClassName="w-full [&>button]:w-full [&>button]:justify-start" />
        )}

        {/* ===== DIVIDER ===== */}
        <HorizontalLine className="my-4" />

        {/* ===== MAIN NAVIGATION ===== */}
        <div className="relative w-full">
          <NavItemButton
            item={{ title: "Dashboard", href: "/dashboard", matchPatterns: ["/dashboard/:id"] }}
            onNavigationClick={onNavigationClick}
          />
          <CountBadge
            count={incompleteActionCount}
            className="absolute top-1/2 right-2 -translate-y-1/2 translate-x-0"
          />
        </div>
        {programPath && (
          <Button
            as={NavLink}
            to={programPath}
            onClick={(e: React.MouseEvent<HTMLAnchorElement>) =>
              onNavigationClick(e, programPath)
            }
            variant="ghost"
            className={twMerge(
              "w-full justify-start",
              (programMatch ||
                (programTabMatch &&
                  !profileMatch &&
                  !resourcesMatch &&
                  !invitationsMatch)) &&
                "bg-brand-100 rounded-full"
            )}
            data-testid="dashboard-nav-program"
          >
            Program
          </Button>
        )}
        {profilePath && (
          <Button
            as={NavLink}
            to={profilePath}
            onClick={(e: React.MouseEvent<HTMLAnchorElement>) =>
              onNavigationClick(e, profilePath)
            }
            variant="ghost"
            className={twMerge(
              "w-full justify-start",
              (profileMatch || profileTabMatch) && "bg-brand-100 rounded-full"
            )}
            data-testid="dashboard-nav-profile"
          >
            {profileNavigationItem.title}
          </Button>
        )}
        {invitationsPath && (
          <Button
            as={NavLink}
            to={invitationsPath}
            onClick={(e: React.MouseEvent<HTMLAnchorElement>) =>
              onNavigationClick(e, invitationsPath)
            }
            variant="ghost"
            className={twMerge(
              "w-full justify-start",
              invitationsMatch && "bg-brand-100 rounded-full"
            )}
            data-testid="dashboard-nav-invitations"
          >
            Invitations
          </Button>
        )}
        {resourcesPath && (
          <Button
            as={NavLink}
            to={resourcesPath}
            onClick={(e: React.MouseEvent<HTMLAnchorElement>) =>
              onNavigationClick(e, resourcesPath)
            }
            variant="ghost"
            className={twMerge(
              "w-full justify-start",
              resourcesMatch && "bg-brand-100 rounded-full"
            )}
            data-testid="dashboard-nav-resources"
          >
            Resources
          </Button>
        )}
        {hasVideoMeetings && (
          <Button
            as={NavLink}
            to="/video"
            onClick={(e: React.MouseEvent<HTMLAnchorElement>) =>
              onNavigationClick(e, "/video")
            }
            variant="ghost"
            className={twMerge(
              "w-full justify-start",
              videoMatch && "bg-brand-100 rounded-full"
            )}
            data-testid="dashboard-nav-video"
          >
            Video
          </Button>
        )}
        {!isSupportPageDisabled && supportPath && (
          <Button
            as={NavLink}
            to={supportPath}
            onClick={(e: React.MouseEvent<HTMLAnchorElement>) =>
              onNavigationClick(e, supportPath)
            }
            variant="ghost"
            className={twMerge(
              "w-full justify-start",
              (supportMatch || supportArticleMatch) && "bg-brand-100 rounded-full"
            )}
            data-testid="dashboard-nav-support"
          >
            Support
          </Button>
        )}

        {/* ===== ADMIN SECTION (conditional) ===== */}
        {organizations.length > 0 && (
          <>
            <HorizontalLine className="my-4" />
            <div className="flex items-center gap-1.5 px-4 mb-2">
              <Folder className="w-4 h-4 text-brand-700" />
              <Type variant="small" className="uppercase font-medium">
                Organizations
              </Type>
            </div>
            {organizations.map((org) => {
              const path = `/program/${org.programId}/organization/${org.id}/admin`;
              const isActive =
                (orgAdminMatch?.params.orgId === String(org.id)) ||
                (orgAdminTabMatch?.params.orgId === String(org.id));
              return (
                <Button
                  key={org.id}
                  as={NavLink}
                  to={path}
                  onClick={(e: React.MouseEvent<HTMLAnchorElement>) =>
                    onNavigationClick(e, path)
                  }
                  variant="ghost"
                  className={twMerge(
                    "w-full justify-start",
                    isActive && "bg-brand-100 rounded-full"
                  )}
                  data-testid="dashboard-nav-organization-admin"
                >
                  {org.name ?? "Organization Admin"}
                </Button>
              );
            })}
          </>
        )}
      </nav>

      {/* ===== FOOTER SECTION ===== */}
      <div className="flex flex-col w-full justify-start items-start gap-2">
        <HorizontalLine className="mb-4" />

        {/* User avatar dropdown with Account and Logout */}
        {user && (
          <PlatformAvatarDropdown
            photo={user.photo ?? ""}
            fullName={fullName}
            isLoaded={true}
            onNavigationClick={onNavigationClick}
            placement="top"
            showNameInTrigger
            menuItems={[
              { title: "Account", href: "/account" },
              { title: "Logout", href: "/logout" },
            ]}
            triggerClassName="w-full mt-2"
            className="w-full py-2 px-2 rounded-full hover:bg-brand-100 transition-colors"
            isActive={!!accountMatch}
          />
        )}
      </div>
    </>
  );
}

export default DashboardNavigationItems;


